import { useState, useEffect, useCallback } from 'react';
import type { ChatMediaItem } from '@/lib/aiChatMedia';

interface Props {
  media: ChatMediaItem[];
  alt: string;
  /** Hauteur de la zone principale (classes Tailwind). */
  coverClassName?: string;
  /** Affiche la grille de miniatures sous l'image principale. */
  showThumbnails?: boolean;
  /** Image de repli (ex. logo de la boutique) si le résultat n'a aucune image. */
  fallbackUrl?: string;
}

/**
 * Galerie média du chat IA : plusieurs images ET vidéos, avec visionneuse
 * plein écran (navigation clavier + lecture vidéo). Tout reste dans le chat.
 */
export default function ChatMediaGallery({ media, alt, coverClassName, showThumbnails = true, fallbackUrl }: Props) {
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const total = media.length;

  useEffect(() => {
    if (index >= total) setIndex(0);
  }, [total, index]);

  const go = useCallback((delta: number) => {
    setIndex((prev) => {
      const next = prev + delta;
      if (next < 0 || next >= total) return prev;
      return next;
    });
  }, [total]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen, go]);

  if (total === 0) {
    return (
      <div className={`w-full rounded-lg bg-background-100 flex items-center justify-center overflow-hidden ${coverClassName || 'h-48'}`}>
        {fallbackUrl ? (
          <img src={fallbackUrl} alt={alt} className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-background-200/70 flex items-center justify-center">
              <i className="ri-image-line text-xl text-foreground-400"></i>
            </div>
            <span className="text-xs text-foreground-400">Aucune image</span>
          </div>
        )}
      </div>
    );
  }

  const current = media[index];

  return (
    <div className="space-y-2">
      {/* Média principal */}
      <div className={`relative w-full rounded-lg overflow-hidden bg-background-100 group ${coverClassName || 'h-48'}`}>
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            controls
            playsInline
            className="w-full h-full object-contain bg-black/90"
          />
        ) : (
          <>
            <img
              src={current.url}
              alt={`${alt} - média ${index + 1}`}
              className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
              onClick={() => setFullscreen(true)}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
              <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-2xl transition-opacity drop-shadow-lg"></i>
            </div>
          </>
        )}

        {/* Navigation entre médias */}
        {total > 1 && (
          <>
            {index > 0 && (
              <button
                type="button"
                onClick={() => go(-1)}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer z-10"
                aria-label="Média précédent"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
            )}
            {index < total - 1 && (
              <button
                type="button"
                onClick={() => go(1)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer z-10"
                aria-label="Média suivant"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            )}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-[11px] font-medium whitespace-nowrap z-10">
              {index + 1} / {total}
            </div>
          </>
        )}

        {/* Badge vidéo */}
        {current.type === 'video' && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-semibold z-10">
            <i className="ri-video-line text-[10px]"></i>Vidéo
          </span>
        )}
      </div>

      {/* Miniatures */}
      {showThumbnails && total > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {media.map((m, i) => (
            <button
              key={`${m.url}-${i}`}
              type="button"
              onClick={() => setIndex(i)}
              className={`relative w-12 h-12 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all cursor-pointer ${
                i === index ? 'border-accent-500' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              {m.type === 'video' ? (
                <span className="w-full h-full flex items-center justify-center bg-foreground-900 text-background-50">
                  <i className="ri-play-fill"></i>
                </span>
              ) : (
                <img
                  src={m.url}
                  alt={`Miniature ${i + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Visionneuse plein écran */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90"
          onClick={() => setFullscreen(false)}
        >
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer z-10"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {index > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/35 transition-colors cursor-pointer z-10"
              aria-label="Précédent"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
          )}
          {index < total - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/35 transition-colors cursor-pointer z-10"
              aria-label="Suivant"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          )}

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium z-10 whitespace-nowrap">
            {index + 1} / {total}
          </div>

          {current.type === 'video' ? (
            <video
              src={current.url}
              controls
              autoPlay
              playsInline
              className="max-w-[90vw] max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={current.url}
              alt={`${alt} - vue agrandie ${index + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg select-none"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      )}
    </div>
  );
}